/* ══════════════════════════════════════════════════════
   STEPS — single source of truth for the 5-stage tracker.

   Names align with The Karat Path (7-step methodology
   shown on the landing page and in StagesTab's header).
   Steps 1–5 are trackable; 6 (Optimization) and 7
   (Legacy) are surfaced as "coming with Planner tier"
   in the UI and don't appear here.

   `calcCurrentStage` is pure: given a state shape, it
   returns { stageResults, currentStage, stagePct } the
   same way StagesTab used to compute it inline. UI
   consumers call `useCurrentStage()` (hook) which
   assembles the state from stores and memoizes the
   result.

   Spec: 2026-04-29 week-4 reconciliation plan §4.2.
   ══════════════════════════════════════════════════════ */

export const STAGES = [
  { num: 1, name: 'Attention', emoji: '👁️',
    desc: "The whole practice starts here. You log what you spent, glance at your score, and move on. The habit of attention is the engine.",
    steps: [
      { id: 's1a', label: 'Income entered',                check: d => d.income > 0 },
      { id: 's1b', label: 'At least 5 transactions logged', check: d => d.txnCount >= 5 },
      { id: 's1c', label: 'One budget strategy applied',    check: d => d.hasBudget },
      { id: 's1d', label: 'One recurring bill added',       check: d => d.hasRecurring },
    ],
  },
  { num: 2, name: 'Clarity', emoji: '🔍',
    desc: 'Your foundation is solid. Bills are covered, you have a starter emergency fund, and debt is under control.',
    steps: [
      { id: 's2a', label: '7-day logging streak',          check: d => d.streak >= 7 },
      { id: 's2b', label: 'Emergency fund goal created',   check: d => d.hasEmergencyGoal },
      { id: 's2c', label: 'Savings rate above 10%',        check: d => d.savRate >= 10 },
      { id: 's2d', label: 'Card debt below 50% utilization', check: d => d.cardUtil < 50 },
    ],
  },
  { num: 3, name: 'Armor', emoji: '🛡️',
    desc: "Wealth without armor is a house of cards. The layer most apps skip — emergency fund sized to your real expenses, insurance basics, and the math behind 'what if income disappears tomorrow.'",
    steps: [
      { id: 's3a', label: '30-day logging streak',          check: d => d.streak >= 30 },
      { id: 's3b', label: 'Savings rate above 20%',         check: d => d.savRate >= 20 },
      { id: 's3c', label: 'At least 2 active goals',        check: d => d.goalCount >= 2 },
      { id: 's3d', label: 'Net worth positive',             check: d => d.netWorth > 0 },
    ],
  },
  { num: 4, name: 'Acceleration', emoji: '🚀',
    desc: 'Once your foundation is solid, accelerate. The math nobody shows you in school: which card to attack first, what an extra $200/mo actually buys you, and how to make every dollar earn its keep.',
    steps: [
      { id: 's4a', label: 'Savings rate above 25%',                  check: d => d.savRate >= 25 },
      { id: 's4b', label: '90-day logging streak',                   check: d => d.streak >= 90 },
      { id: 's4c', label: 'Net worth above $10,000',                 check: d => d.netWorth >= 10000 },
      { id: 's4d', label: 'All credit cards below 30% utilization',  check: d => d.cardUtil < 30 },
    ],
  },
  { num: 5, name: 'Growth', emoji: '📈',
    desc: 'Your savings rate is the engine. Watch it over time — assets up, debts down, the gap widening into wealth. Net worth as a story, not a snapshot.',
    steps: [
      { id: 's5a', label: 'Savings rate above 30%',          check: d => d.savRate >= 30 },
      { id: 's5b', label: '180-day logging streak',          check: d => d.streak >= 180 },
      { id: 's5c', label: 'All goals funded or completed',   check: d => d.goalsPct >= 80 },
      { id: 's5d', label: 'Net worth above $50,000',         check: d => d.netWorth >= 50000 },
    ],
  },
];

/**
 * Pure: takes a state shape `d` and computes per-stage progress + the
 * user's current stage (highest fully-completed stage + 1, capped at 5).
 *
 * @param {{
 *   income: number, savRate: number, streak: number, txnCount: number,
 *   hasBudget: boolean, hasRecurring: boolean, hasEmergencyGoal: boolean,
 *   cardUtil: number, goalCount: number, netWorth: number, goalsPct: number,
 * }} d
 * @returns {{ stageResults: Array, currentStage: number, stagePct: number }}
 */
export function calcCurrentStage(d) {
  const stageResults = STAGES.map(st => {
    const steps = st.steps.map(s => ({ ...s, done: !!s.check(d) }));
    const pct = steps.filter(s => s.done).length / steps.length;
    return { ...st, steps, pct };
  });
  let currentStage = 1;
  for (let i = 0; i < stageResults.length; i++) {
    if (stageResults[i].pct >= 1) currentStage = Math.min(i + 2, STAGES.length);
  }
  const cs = stageResults[currentStage - 1];
  return { stageResults, currentStage, stagePct: cs ? cs.pct : 0 };
}
